import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useReveal } from "../lib/useReveal";
import { HandHeart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  "Animal Care",
  "Rescue Support",
  "Veterinary Assistance",
  "Fundraising & Awareness",
  "Photography / Storytelling",
];

export default function Volunteer() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    interest: interests[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const ref = useReveal();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.interest) {
      toast.error("Please fill your name, phone and area of interest");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/volunteer`, {
        ...form,
        email: form.email || undefined,
      });
      toast.success("Thank you for offering your time", {
        description: "Our team will reach out within 48 hours.",
      });
      setForm({ name: "", phone: "", email: "", interest: interests[0], message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="volunteer"
      data-testid="volunteer-section"
      className="py-24 md:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="reveal grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-[#D4AF37]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#4A5D53]">Volunteer</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1A2E24] leading-[1.05]">
              Give a <span className="italic text-[#1B4D3E]">Sunday</span>.
              <br />Save a life.
            </h2>
            <p className="mt-6 text-[#4A5D53] leading-relaxed">
              Spend a morning walking cows, assisting a surgery, feeding calves
              or simply telling their stories. We welcome all hands — and hearts.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-3">
              {interests.map((i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-[#1A2E24]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> {i}
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="lg:col-span-7 bg-[#F4F1EA] border border-[#E3DECE] rounded-3xl p-8 md:p-12"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#4A5D53]">Name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="volunteer-name"
                  className="mt-2 w-full bg-[#FDFBF7] border border-[#E3DECE] rounded-full px-5 py-3 outline-none focus:border-[#1B4D3E]"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#4A5D53]">Phone</label>
                <input
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="volunteer-phone"
                  className="mt-2 w-full bg-[#FDFBF7] border border-[#E3DECE] rounded-full px-5 py-3 outline-none focus:border-[#1B4D3E]"
                  placeholder="+91…"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#4A5D53]">Email (optional)</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="volunteer-email"
                  className="mt-2 w-full bg-[#FDFBF7] border border-[#E3DECE] rounded-full px-5 py-3 outline-none focus:border-[#1B4D3E]"
                  placeholder="you@example.com"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#4A5D53]">Area of interest</label>
                <select
                  value={form.interest}
                  onChange={(e) => setForm({ ...form, interest: e.target.value })}
                  data-testid="volunteer-interest"
                  className="mt-2 w-full bg-[#FDFBF7] border border-[#E3DECE] rounded-full px-5 py-3 outline-none focus:border-[#1B4D3E]"
                >
                  {interests.map((i) => (
                    <option key={i}>{i}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-[11px] tracking-[0.22em] uppercase text-[#4A5D53]">Why you'd like to help (optional)</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="volunteer-message"
                  className="mt-2 w-full bg-[#FDFBF7] border border-[#E3DECE] rounded-2xl px-5 py-3 outline-none focus:border-[#1B4D3E] resize-none"
                  placeholder="A line or two is enough…"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="volunteer-submit-btn"
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#1B4D3E] hover:bg-[#143C30] disabled:opacity-60 text-[#FDFBF7] px-7 py-4 rounded-full font-medium transition-all"
            >
              <HandHeart className="w-4 h-4 text-[#D4AF37]" />
              {submitting ? "Submitting…" : "Count me in"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
